import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { BenderAssetType, STRICT_THUMBNAIL_ASPECT_RATIO_PUBLISHERS, PUBLISHER_THUMBNAIL_REQUIREMENTS } from 'src/constants';
import { Configuration, ImageTemplatePreview, type TemplateSelectProps, VideoTemplateMetadata, VideoTemplateThumbnail } from 'src/components/campaigns/forms/TemplateSelectUtils';
import { type Template } from 'src/types/template.types';
import { type CampaignDesiredStateDto, type DetailPageLinkConfig, type HomePageLinkConfig, type LandingPageLinkConfig } from 'src/store/generated/leela-api-generated';
import { DestinationLinkConfiguration } from 'src/components/campaigns/forms/DestinationLinkConfiguration';
import { GenerateAllOrderingsCheckbox } from 'src/components/campaigns/forms/GenerateAllOrderingsCheckbox';

// Type alias for destination link config union
type DestinationLinkConfig = DetailPageLinkConfig | HomePageLinkConfig | LandingPageLinkConfig;

export const TemplateSelect: React.FC<TemplateSelectProps> = (props) => {
  const { control, name, templates, testId, index } = props;
  const { getValues, setValue, register, unregister, trigger } = useFormContext();

  const assetTypeFieldName = useMemo(() => `${name}.assetType`, [name]);
  const templateIdFieldName = useMemo(() => `${name}.templateId`, [name]);
  const templateConfigName = useMemo(() => `${name}.config`, [name]);
  const videoDurationName = useMemo(() => `${templateConfigName}.videoDurations`, [templateConfigName]);
  const thumbnailTemplateIdName = useMemo(() => `${templateConfigName}.thumbnailTemplateId`, [templateConfigName]);
  const destinationLinkConfigName = useMemo(() => `${name}.destinationLinkConfig`, [name]);
  const productStrategyName = useMemo(() => name.split('.benderTemplates.')[0], [name]);

  const templateId = useWatch({ control, name: templateIdFieldName });
  const businessLine = useWatch({ control, name: `${productStrategyName}.businessLine` });

  const selectedTemplate = useMemo(() => {
    if (templateId) {
      return templates.find((template) => template.id === templateId);
    }
  }, [templateId, templates]);

  const initialTemplateId = useRef(selectedTemplate?.id);

  const publisher: CampaignDesiredStateDto['publisher'] = useWatch({ control, name: 'publisher' });

  // Get the parent field name to access all bender templates
  const parentFieldName = useMemo(() => name.replace(/\.\d+$/, ''), [name]);

  // Watch for changes to the current destination link config for copying to other templates
  const currentDestinationLinkConfig = useWatch({ control, name: destinationLinkConfigName }) as DestinationLinkConfig;

  // Local state for the "use for all" checkbox (not part of form data)
  const [useForAll, setUseForAll] = useState(false);

  // Function to copy destination link config to all other multi-title templates
  const copyConfigToAllMultiTitleTemplates = useCallback((config: DestinationLinkConfig) => {
    const allTemplates = getValues(parentFieldName) || [];
    allTemplates.forEach((template: { templateId?: string }, templateIndex: number) => {
      if (templateIndex !== index && template?.templateId) {
        const templateData = templates.find(t => t.id === template.templateId);
        if (templateData?.metadata?.groupSize) {
          setValue(`${parentFieldName}.${templateIndex}.destinationLinkConfig`, config);
        }
      }
    });
  }, [getValues, setValue, parentFieldName, index, templates]);

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }
    if (initialTemplateId.current === undefined) {
      initialTemplateId.current = selectedTemplate.id;
    }

    if (selectedTemplate.assetType === BenderAssetType.VIDEO) {
      register(videoDurationName);
      register(thumbnailTemplateIdName);

      if (initialTemplateId.current !== undefined &&
        initialTemplateId.current !== selectedTemplate.id) {
        setValue(videoDurationName, []);
        setValue(thumbnailTemplateIdName, undefined);
        trigger();
      }
    } else {
      unregister(videoDurationName);
      unregister(thumbnailTemplateIdName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, name]);

  const isVideoTemplate = useMemo(() => selectedTemplate?.assetType === BenderAssetType.VIDEO, [selectedTemplate]);
  const isMultiTitleTemplate = useMemo(() => !!selectedTemplate?.metadata?.groupSize, [selectedTemplate]);

  // Check if this is the first multi-title template in the form
  const isFirstMultiTitleTemplate = useMemo(() => {
    if (!isMultiTitleTemplate) return false;

    const allTemplates = getValues(parentFieldName) || [];

    // Check if any template before this index is also multi-title
    return !allTemplates.slice(0, index).some((template: { templateId?: string }) => {
      if (!template?.templateId) return false;
      const templateData = templates.find(t => t.id === template.templateId);
      return !!templateData?.metadata?.groupSize;
    });
  }, [isMultiTitleTemplate, index, parentFieldName, getValues, templates]);

  const thumbnailTemplateOptions = useMemo(() => {
    if (isVideoTemplate && PUBLISHER_THUMBNAIL_REQUIREMENTS[publisher]) {
      return templates.filter(template =>
        template.assetType === BenderAssetType.IMAGE && (
          STRICT_THUMBNAIL_ASPECT_RATIO_PUBLISHERS.includes(publisher) ? selectedTemplate && template.aspectRatio === selectedTemplate.aspectRatio : true
        ));
    }
    return undefined;
  }, [isVideoTemplate, publisher, selectedTemplate, templates]);

  const videoDurations = useMemo(() => selectedTemplate?.templateSchema?.properties?.videoDurations, [selectedTemplate]);

  const hasTimezone = useMemo(
    () => !!selectedTemplate?.templateSchema?.properties?.overrideAssetTimezone,
    [selectedTemplate],
  );

  // Watch for destination link config changes and propagate if "use for all" is checked
  useEffect(() => {
    if (useForAll && currentDestinationLinkConfig && isFirstMultiTitleTemplate) {
      copyConfigToAllMultiTitleTemplates(currentDestinationLinkConfig);
    }
  }, [useForAll, currentDestinationLinkConfig, isFirstMultiTitleTemplate, copyConfigToAllMultiTitleTemplates]);

  // Watch for template array changes and copy config to newly added multi-title templates
  const watchedTemplates = useWatch({ control, name: parentFieldName });
  const allTemplates = useMemo(() => watchedTemplates || [], [watchedTemplates]);
  const templateCount = useMemo(() => allTemplates.length, [allTemplates.length]);
  const prevTemplateCount = useRef(templateCount);

  // Track which templates have templateIds to detect when new ones get assigned
  const templatesWithIds = useMemo(() =>
    allTemplates.filter((template: { templateId?: string }) => template?.templateId).length,
  [allTemplates]
  );
  const prevTemplatesWithIds = useRef(templatesWithIds);

  useEffect(() => {
    // Copy when templates are added OR when existing templates get templateIds assigned
    if (useForAll && currentDestinationLinkConfig && isFirstMultiTitleTemplate && (
      templateCount > prevTemplateCount.current ||
        templatesWithIds > prevTemplatesWithIds.current
    )) {
      copyConfigToAllMultiTitleTemplates(currentDestinationLinkConfig);
    }
    prevTemplateCount.current = templateCount;
    prevTemplatesWithIds.current = templatesWithIds;
  }, [templateCount, templatesWithIds, useForAll, currentDestinationLinkConfig, isFirstMultiTitleTemplate, copyConfigToAllMultiTitleTemplates]);

  /** Updates form values when template selection changes */
  useEffect(() => {
    if (selectedTemplate) {
      setValue(assetTypeFieldName, selectedTemplate.assetType);

      const currentConfig = getValues(`${name}.config`) ?? {};
      const isMultiTitle = !!selectedTemplate.metadata?.groupSize;

      const newConfig = {
        ...currentConfig,
        width: selectedTemplate?.width,
        height: selectedTemplate?.height,
      };

      setValue(`${name}.config`, newConfig);
      setValue(`${name}.templateType`, isMultiTitle ? 'MULTI_TITLE' : 'SINGLE_TITLE');

      if (isMultiTitle) {
        const isChannel = businessLine === 'CHANNEL';
        setValue(`${name}.groupingStrategy`, isChannel ? 'ATTRIBUTE' : 'DEFAULT');
        setValue(`${name}.groupByAttribute`, isChannel ? 'channels' : undefined);
        setValue(`${name}.groupSize`, selectedTemplate.metadata.groupSize);
        // Set default destination link config for multi-title templates
        const existingConfig = getValues(destinationLinkConfigName);
        if (!existingConfig) {
          setValue(destinationLinkConfigName, { type: 'HOME' });
        }
        // Inherit generateAllOrderings from the first multi-title template (only for newly added templates)
        const existingGenerateAll = getValues(`${name}.generateAllOrderings`);
        if (existingGenerateAll === undefined || existingGenerateAll === null) {
          const allTmplValues = getValues(parentFieldName) || [];
          const inheritedValue = allTmplValues.some((tmpl: { templateId?: string; generateAllOrderings?: boolean }, i: number) => {
            if (i === index || !tmpl?.templateId) return false;
            const meta = templates.find(t => t.id === tmpl.templateId)?.metadata;
            return meta?.groupSize && tmpl.generateAllOrderings;
          });
          setValue(`${name}.generateAllOrderings`, inheritedValue);
        }
        // Reset the local checkbox state when switching templates
        setUseForAll(false);
      } else {
        // Clear multi-title properties when switching to single-title
        setValue(`${name}.groupingStrategy`, undefined);
        setValue(`${name}.groupByAttribute`, undefined);
        setValue(`${name}.groupSize`, undefined);
        setValue(`${name}.generateAllOrderings`, undefined);
        // Set default destination link config for single-title templates
        setValue(destinationLinkConfigName, { type: 'DETAIL' });
        setUseForAll(false);
      }

      // Trigger validation after setting values
      trigger();
    }
  }, [assetTypeFieldName, selectedTemplate, setValue, name, getValues, trigger, destinationLinkConfigName]);

  /** Updates grouping strategy when businessLine changes after template is already selected */
  useEffect(() => {
    if (!selectedTemplate?.metadata?.groupSize) return;
    const isChannel = businessLine === 'CHANNEL';
    setValue(`${name}.groupingStrategy`, isChannel ? 'ATTRIBUTE' : 'DEFAULT');
    setValue(`${name}.groupByAttribute`, isChannel ? 'channels' : undefined);
  }, [businessLine, selectedTemplate, setValue, name]);

  return (
    <Column>
      <TemplatesDropdown
        {...props}
        destinationLinkConfigName={destinationLinkConfigName}
        isFirstMultiTitleTemplate={isFirstMultiTitleTemplate}
        name={templateIdFieldName}
        useForAll={useForAll}
        onUseForAllChange={setUseForAll}
      />
      <Configuration
        {...props}
        countryCode={props.countryCode}
        hasTimezone={hasTimezone}
        thumbnailTemplateOptions={thumbnailTemplateOptions}
        videoDurations={videoDurations}
      />
      <input
        {...register(assetTypeFieldName)}
        data-testid={`${testId}-asset-type`}
        type="hidden"
      />
    </Column>
  );
};

export const TemplatesDropdown: React.FC<TemplateSelectProps> = ({
  control,
  disabled,
  index,
  isThumbnailTemplate,
  label,
  name,
  templates,
  testId,
  destinationLinkConfigName,
  isFirstMultiTitleTemplate,
  useForAll,
  onUseForAllChange,
  ...meridianProps
}) => {
  const { t } = useTranslation();
  const { field, fieldState } = useController({
    control,
    name,
    rules: { required: true },
  });
  const [searchQuery, setSearchQuery] = React.useState('');

  const publisher: CampaignDesiredStateDto['publisher'] = useWatch({ control, name: 'publisher' });

  const filteredTemplates = useMemo(() => {
    let filteredTemplates = templates;

    // Filter out multi-title templates if publisher doesn't support them
    if (publisher !== 'adsp' && publisher !== 'google') {
      filteredTemplates = filteredTemplates.filter(template => !template.metadata?.groupSize);
    }

    if (!searchQuery) return filteredTemplates;
    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedQuery, 'i');
    return filteredTemplates.filter(template =>
      regex.test(template.title) ||
      regex.test(template.description) ||
      regex.test(template.id)
    );
  }, [templates, searchQuery, publisher]);

  const selectedTemplate = useMemo(() => {
    if (field.value) {
      return templates.find(template => template.id === field.value);
    }
  }, [field.value, templates]);

  // Clear selection when current template becomes invalid due to publisher change
  useEffect(() => {
    if (selectedTemplate?.metadata?.groupSize && publisher !== 'adsp' && publisher !== 'google') {
      field.onChange('');
    }
  }, [publisher, selectedTemplate, field]);

  const isVideoTemplate = useMemo(() => selectedTemplate?.assetType === BenderAssetType.VIDEO, [selectedTemplate]);
  const isMultiTitleTemplate = useMemo(() => !!selectedTemplate?.metadata?.groupSize, [selectedTemplate]);

  return (
    <Row
        alignmentHorizontal="justify"
        alignmentVertical="top"
        widths={isThumbnailTemplate ? ['75%', '25%'] : ['60%', '40%']}
      >
      <Column>
        <Select
          {...meridianProps}
          data-testid={testId}
          disabled={disabled}
          error={!!fieldState.error}
          errorMessage={fieldState.error?.message}
          label={label}
          name={name}
          placeholder={t('form_bender_template_placeholder')}
          searchQuery={searchQuery}
          value={field.value}
          onBlur={field.onBlur}
          onChange={field.onChange}
          onSearch={setSearchQuery}
        >
          {!filteredTemplates.length &&
            <SelectOption disabled label={t('table_no_results')} value={t('table_no_results')}></SelectOption>
          }
          {filteredTemplates.length && filteredTemplates.map((template) => {
            return (
              <SelectOption
                data-testid={`template-select-option-${template.id}`}
                key={template.id}
                label={template.title}
                value={template.id}
              >
                {({ preselected, selected }) => (
                  <TemplateSelectOption
                    preselected={preselected}
                    selected={selected}
                    template={template}
                  />
                )}
              </SelectOption>
            );
          })}
        </Select>
        {isMultiTitleTemplate && destinationLinkConfigName && (
          <DestinationLinkConfiguration
            control={control}
            index={index}
            isFirstMultiTitleTemplate={isFirstMultiTitleTemplate ?? false}
            name={destinationLinkConfigName}
            useForAll={useForAll ?? false}
            onUseForAllChange={onUseForAllChange ?? (() => {})}
          />
        )}
        {isMultiTitleTemplate && (
          <GenerateAllOrderingsCheckbox
            control={control}
            index={index}
            isFirstMultiTitleTemplate={isFirstMultiTitleTemplate ?? false}
            name={name.replace(/\.templateId$/, '.generateAllOrderings')}
            parentFieldName={name.replace(/\.\d+\.templateId$/, '')}
            templates={templates}
          />
        )}
      </Column>
      <Column>
        {selectedTemplate && (
          isVideoTemplate
            ? <VideoTemplateMetadata index={index} template={selectedTemplate} />
            : <ImageTemplatePreview index={index} isThumbnailTemplate={isThumbnailTemplate} previewImageUrl={selectedTemplate.previewImageUrl} />
        )}
      </Column>
    </Row>
  );
};

export const TemplateSelectOption = ({
  preselected,
  selected,
  template,
}: {
  preselected: boolean;
  selected: boolean;
  template: Template;
}) => {
  const secondaryColor = preselected || selected ? 'inverted' : 'secondary';
  const { aspectRatio, assetType, description, id, previewImageUrl, title } = template;

  return (
    <Row widths={['160px', 'fill']}>
      <Column alignmentHorizontal="center">
        {assetType === BenderAssetType.IMAGE
          ? (
          <img data-testid={`template-select-option-image-${id}`} src={previewImageUrl} style={{ maxHeight: 160, maxWidth: 160 }} />
            )
          : (
          <VideoTemplateThumbnail aspectRatio={aspectRatio} />
            )}
      </Column>
      <Column spacing="200">
        <Text>
          {title}
        </Text>
        <Text color={secondaryColor} type="b200">
          {description}
        </Text>
        <Text color={secondaryColor} type="b100">
          id: {id}
        </Text>
      </Column>
    </Row>
  );
};
